import { ISuggestedTemplate, ITemplateBandOption } from '../types/format-template.types';

// A banded template composes its stages per skater-count band and stores no blueprints, so the
// surfaces that render or fetch a stage list have to branch on this together.
export const isBandedTemplate = (template?: IFormatTemplate | null): boolean => !!template?.bands?.length;

// An unset bound is open-ended, so a range with neither bound covers every roster.
const coversPlayerCount = (min: number | undefined, max: number | undefined, count: number): boolean =>
  (min == null || count >= min) && (max == null || count <= max);

// Distance from a roster count to a band's range; 0 when the band covers the count.
const bandDistance = (band: IFormatTemplateBand, count: number): number => {
  if (band.minPlayers != null && count < band.minPlayers) return band.minPlayers - count;
  if (band.maxPlayers != null && count > band.maxPlayers) return count - band.maxPlayers;
  return 0;
};

// First template tagged for this age group whose range covers the roster's non-goalie (skater)
// count — template/band bounds never count goalies, matching the server's band selection.
export const suggestTemplate = (
  templates: IFormatTemplate[],
  ageGroupBucket: string | null,
  nonGoalieCount?: number,
): ISuggestedTemplate | null => {
  if (!ageGroupBucket || nonGoalieCount == null) return null;
  const inBucket = templates.filter(t => t.ageGroupBuckets?.includes(ageGroupBucket));
  for (const template of inBucket) {
    if (template.bands?.length) {
      const band = template.bands.find(b => coversPlayerCount(b.minPlayers, b.maxPlayers, nonGoalieCount));
      if (band) return { template, bandKey: band.key };
    } else if (coversPlayerCount(template.minPlayers, template.maxPlayers, nonGoalieCount)) {
      return { template, bandKey: null };
    }
  }
  // A roster outside every band clamps to the numerically nearest band across the bucket's
  // banded templates — the same distance rule the server applies when no band is named.
  let nearest: { template: IFormatTemplate; bandKey: string; distance: number; min: number } | null = null;
  for (const template of inBucket) {
    for (const band of template.bands ?? []) {
      const distance = bandDistance(band, nonGoalieCount);
      const min = band.minPlayers ?? 0;
      // A distance tie between a band below the count and one above it goes to the smaller band.
      if (!nearest || distance < nearest.distance || (distance === nearest.distance && min < nearest.min)) {
        nearest = { template, bandKey: band.key, distance, min };
      }
    }
  }
  return nearest && { template: nearest.template, bandKey: nearest.bandKey };
};

// Templates with no ageGroupBuckets land under this catch-all bucket.
export const OTHER_BUCKET = 'Other';

// Leading age-group descriptor: one or more U# tokens (/, &, comma joined) up to
// the em/en dash or hyphen separator — e.g. "U9/U11 — 10 to 14 Skaters".
const BUCKET_PREFIX = /^\s*U\d+(?:\s*[/&,]\s*U\d+)*\s*[—–-]\s*/i;

// Drop the age-group prefix from a template name so the option reads as just its
// distinguishing part (the skater range); returns the original if nothing matched.
export const stripBucketPrefix = (name: string): string =>
  name.replace(BUCKET_PREFIX, '').trim() || name;

// Fan a template into every bucket it supports, so a multi-bucket template
// appears under each of its buckets; bucketless templates fall to OTHER_BUCKET.
export const groupTemplatesByBucket = (
  templates: IFormatTemplate[],
): Map<string, IFormatTemplate[]> => {
  const map = new Map<string, IFormatTemplate[]>();
  templates.forEach(t => {
    const buckets = t.ageGroupBuckets?.length ? t.ageGroupBuckets : [OTHER_BUCKET];
    buckets.forEach(b => {
      const list = map.get(b) ?? [];
      list.push(t);
      map.set(b, list);
    });
  });
  return map;
};

// One select value per applyable choice — a band rides its template's id so a flat option list
// can round-trip both fields through a single-select control.
export const templateBandOptionValue = (templateId: string, bandKey?: string | null): string =>
  (bandKey ? `${templateId}:${bandKey}` : templateId);

// Fan each banded template into one option per band — a catalog family is a set of per-skater-count
// templates composed on the fly at preview/apply, never one applyable template itself.
export const buildTemplateBandOptions = (templates: IFormatTemplate[]): ITemplateBandOption[] => {
  const bandedCount = templates.filter(t => isBandedTemplate(t)).length;
  return templates.flatMap((template): ITemplateBandOption[] => {
    if (!isBandedTemplate(template)) {
      return [{
        value: templateBandOptionValue(template._id),
        templateId: template._id,
        bandKey: null,
        label: stripBucketPrefix(template.name),
      }];
    }
    return (template.bands ?? []).map(band => ({
      value: templateBandOptionValue(template._id, band.key),
      templateId: template._id,
      bandKey: band.key,
      // Band labels are only ambiguous when several banded templates share the list; prefix then.
      label: bandedCount > 1
        ? `${stripBucketPrefix(template.name)} — ${band.label || band.key}`
        : (band.label || band.key),
    }));
  });
};

// Sort U# buckets by their numeric age ascending; OTHER_BUCKET (and any non-U#) sinks last.
export const sortAgeGroupBuckets = (buckets: string[]): string[] =>
  [...buckets].sort((a, b) => {
    const na = parseInt(a.replace(/\D/g, ''), 10);
    const nb = parseInt(b.replace(/\D/g, ''), 10);
    if (Number.isNaN(na)) return Number.isNaN(nb) ? a.localeCompare(b) : 1;
    if (Number.isNaN(nb)) return -1;
    return na - nb;
  });

// Single owner of the age-group chip options both the create and edit modals offer, so the two
// rows can't derive them differently. `extra` keeps a bucket the template already carries.
export const buildAgeGroupBucketOptions = (known: string[], extra: string[] = []): string[] =>
  sortAgeGroupBuckets(Array.from(new Set([...known, ...extra])));

// Single owner of the apply modal's starting bucket so the container and seed effect can't drift.
// With a recommendation: the age group's bucket if that template covers it, else the template's first.
export const resolveInitialBucket = (
  templates: IFormatTemplate[],
  recommendedTemplate: IFormatTemplate | null,
  ageGroupBucket: string | null,
): string | null => {
  if (recommendedTemplate) {
    const recBuckets = recommendedTemplate.ageGroupBuckets?.length
      ? recommendedTemplate.ageGroupBuckets
      : [OTHER_BUCKET];
    return recBuckets.includes(ageGroupBucket ?? '') ? ageGroupBucket : recBuckets[0];
  }
  // Without one, the age group's own bucket only if it has templates — an empty list is worse
  // than asking the coach to pick.
  return ageGroupBucket && groupTemplatesByBucket(templates).has(ageGroupBucket) ? ageGroupBucket : null;
};

export const buildStageBlueprintsFromStages = (stages: IAssessmentStage[]): IStageBlueprint[] =>
  stages.map((stage, i) => ({
    type: stage.type,
    order: i + 1,
    title: stage.title,
    name: (stage.config as { name?: string })?.name,
    sessionType: (stage.config as { sessionType?: string })?.sessionType,
    groupLabel: (stage.config as { groupLabel?: string })?.groupLabel,
    day: (stage.config as { day?: number })?.day,
    // Store each stage's config verbatim, incl. a SESSION stage's pre-selected drill/practicePlan IDs.
    // Those IDs are company-scoped; drill/plan deletes strip them from templates. See CLAUDE.md.
    config: stage.config as { [key: string]: unknown } | undefined,
  }));

// Inverse of buildStageBlueprintsFromStages: rehydrate blueprints into in-memory stages.
// completed=true → read-only preview render; false → editable config in the edit modal.
export const buildStagesFromBlueprints = (blueprints: IStageBlueprint[], completed = true): IAssessmentStage[] =>
  [...blueprints]
    .sort((a, b) => a.order - b.order)
    .map(bp => ({
      _id: `preview-${bp.order}`,
      order: bp.order,
      type: bp.type,
      title: bp.title,
      config: bp.config ?? {},
      isCompleted: completed,
    })) as unknown as IAssessmentStage[];

// Config keys the backend derives per band at apply time and refuses to store as a catalog stage
// setting: SESSION name/sessionType/day, ROUTE name/playerCount/routes, GROUP groupCount/playerLimits.
export const DERIVED_STAGE_CONFIG_KEYS: { [type: string]: string[] } = {
  SESSION: ['name', 'sessionType', 'day'],
  ROUTE: ['name', 'playerCount', 'routes'],
  GROUP: ['groupCount', 'playerLimits'],
};

// A setting write is rejected outright if a derived key is present, so drop them before sending.
export const stripDerivedStageConfigKeys = (
  type: string,
  config: { [key: string]: unknown },
): { [key: string]: unknown } => {
  const derived = DERIVED_STAGE_CONFIG_KEYS[type] ?? [];
  return Object.fromEntries(Object.entries(config).filter(([key]) => !derived.includes(key)));
};

// Adapts a catalog family's stage library into in-memory stages for the shared stage editor;
// _id carries the definition key so config edits round-trip as stageSettings refs.
export const buildStagesFromStageLibrary = (
  stageLibrary: IFormatTemplateStageDefinition[],
): IAssessmentStage[] =>
  stageLibrary.map((definition, i) => ({
    _id: definition.key,
    order: i + 1,
    type: definition.type,
    title: definition.title ?? definition.name,
    config: definition.config ?? {},
    isCompleted: false,
  })) as unknown as IAssessmentStage[];

export const isContiguousSelection = (selectedIndices: number[]): boolean => {
  if (selectedIndices.length === 0) return true;
  const sorted = [...selectedIndices].sort((a, b) => a - b);
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i] !== sorted[i - 1] + 1) return false;
  }
  return true;
};

// True when adding `index` would leave a gap (not already selected, adjacent, or first
// pick) — drives a stage checkbox's disabled state so only contiguous runs form.
export const wouldBreakContiguity = (selectedIndices: number[], index: number): boolean => {
  if (selectedIndices.length === 0 || selectedIndices.includes(index)) return false;
  const sorted = [...selectedIndices].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  return index < min - 1 || index > max + 1;
};
